import React from 'react'
import { noop } from 'lodash-es'
import { motion } from 'motion/react'
import { useViewContext } from '@base/ViewContext'
import type { DerivedTimespan as TimespanType } from '../../types/derived_items'
import { useGesture } from '../GestureProvider'
import { DragEventData } from '../../types/gesture-types'
import type { Bounds } from '../../types/types'
import { SPRING_TRANSITION } from '../../constants'
import DefaultTimespanContent from './DefaultTimespanContent'

type TimespanProps = {
  timespan: TimespanType
  x: number
  y: number
  width: number
  height: number
  zIndex: number
  onTap: (id: string, type: 'timespan', bounds: Bounds) => void
  onPanStart: (event: DragEventData) => void
  onPanMove: (event: DragEventData) => void
  onPanEnd: (event: DragEventData) => void
}

export interface TimespanContentProps {
  timespan: TimespanType
}

interface ViewContextValue {
  componentMap?: {
    TimespanContent?: React.ComponentType<TimespanContentProps>
  }
}

const Timespan: React.FC<TimespanProps> = props => {
  const {
    timespan,
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    zIndex,
    onTap = noop,
    onPanStart = noop,
    onPanMove = noop,
    onPanEnd = noop,
  } = props
  const viewContext = useViewContext() as ViewContextValue
  const TimespanContentComponent = viewContext.componentMap?.TimespanContent || DefaultTimespanContent

  const ref = React.useRef<HTMLDivElement | null>(null)
  const { bindGesture, isDragging } = useGesture()
  const handleTap = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const bounds: Bounds = {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      }
      onTap(timespan.id, 'timespan', bounds)
    }
  }

  // Use our gesture context to bind gesture handlers
  const bind = bindGesture(
    {
      onDragStart: ({ event, deltaX, deltaY }) => {
        onPanStart({ event, deltaX, deltaY })
      },
      onDragMove: ({ event, deltaX, deltaY }) => {
        onPanMove({
          event,
          deltaX,
          deltaY,
        })
      },
      onDragEnd: ({ event, deltaX, deltaY }) => {
        onPanEnd({ event, deltaX, deltaY })
      },
      onTap: handleTap,
    },
    {
      axis: 'x',
    }
  )
  // Applies to the wrapper to control z-index (gets around transform creating a new stacking order context and marker position indicators appearing above timespans)
  const wrapperStyle: React.CSSProperties = {
    zIndex: zIndex,
    position: 'relative',
  }
  const timelineItemStyle: React.CSSProperties = {
    width: width,
    height: height,
    transform: `translate(${x}px, 0px)`, // Only apply x here
    userSelect: isDragging ? 'none' : 'auto', // 'auto' is in SCSS, JS handles 'none'
  }

  return (
    <motion.div
      initial={false} // Prevent initial animation
      animate={{ y }} // Animate the y position
      transition={SPRING_TRANSITION} // Use the imported constant
      style={wrapperStyle}
    >
      <div
        className="timeline-item timeline-item--timespan z-depth-1"
        style={timelineItemStyle}
        ref={ref}
        key={`timespan-${timespan.id}`}
        {...bind()}
      >
        <TimespanContentComponent timespan={timespan} />
      </div>
    </motion.div>
  )
}
export default Timespan
